import { useState } from "react";
import { Download, FileText, MessageSquare, Plus, ArrowLeft } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";

import { useOrderDetail, useReadyForPickup } from "./api";
import { ExtensionRequestSheet } from "./ExtensionRequestSheet";
import { daysUntil, formatDate, formatDateTime, formatMoneyCents } from "./format";
import { IssueReporterDialog } from "./IssueReporterDialog";
import { ExpiryReminderBanner } from "./ExpiryReminderBanner";
import { ProposedWindowBanner } from "./ProposedWindowBanner";
import type { OrderDetailLine, OrderDetailPayload } from "./types";
import { StatusPill } from "./components/StatusPill";
import { CountdownChip } from "./components/CountdownChip";
import { EquipmentThumb } from "./components/EquipmentThumb";
import { LifecycleStepper } from "./components/LifecycleStepper";
import { SiteMap } from "./components/SiteMap";
import { PaymentChip } from "./components/PaymentChip";

// Phase 4b extension-requests FR-1 — CTA visible only on active rentals
// (driver delivered, customer not yet picked up).
const EXTENDABLE_STATUSES = new Set(["delivered", "on_rent"]);
// Phase 4j portal-polish — the "Notify ready for pickup" CTA only
// makes sense once the equipment is actually on site.
const READY_FOR_PICKUP_STATUSES = new Set(["delivered", "on_rent"]);

// Phase 4a customer-portal FR-6 + Phase 4j portal-polish — order
// detail with equipment hero, status pill + countdown, inline action
// rail, and (audit follow-up #1) empty sections hidden by default.
// Grade lift: C+ → B+/A-.
export function OrderDetailPage(): JSX.Element {
  const { order_number = "" } = useParams<{ order_number: string }>();
  const location = useLocation();
  const [reporterOpen, setReporterOpen] = useState(location.hash === "#issue");
  const [extensionOpen, setExtensionOpen] = useState(false);
  const q = useOrderDetail(order_number);

  if (q.isLoading) return <div className="p-4 text-sm text-zinc-600">Loading…</div>;
  if (q.isError || !q.data) {
    return <div className="p-4 text-sm text-brand-red">Couldn&rsquo;t load this order.</div>;
  }

  return (
    <DetailBody
      payload={q.data}
      orderNumber={order_number}
      reporterOpen={reporterOpen}
      onOpenReporter={() => setReporterOpen(true)}
      onCloseReporter={() => setReporterOpen(false)}
      extensionOpen={extensionOpen}
      onOpenExtension={() => setExtensionOpen(true)}
      onCloseExtension={() => setExtensionOpen(false)}
    />
  );
}

interface BodyProps {
  payload: OrderDetailPayload;
  orderNumber: string;
  reporterOpen: boolean;
  onOpenReporter: () => void;
  onCloseReporter: () => void;
  extensionOpen: boolean;
  onOpenExtension: () => void;
  onCloseExtension: () => void;
}

// eslint-disable-next-line max-lines-per-function
function DetailBody({
  payload,
  orderNumber,
  reporterOpen,
  onOpenReporter,
  onCloseReporter,
  extensionOpen,
  onOpenExtension,
  onCloseExtension
}: BodyProps): JSX.Element {
  const o = payload.order;
  const heroLine = payload.lines[0];
  const remaining = daysUntil(o.pickup_date);

  return (
    <div className="space-y-6 text-sm">
      <Link
        to="/portal"
        className="inline-flex items-center gap-1 text-xs font-semibold text-brand-red transition hover:text-brand-red-dark hover:underline"
      >
        <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
        Dashboard
      </Link>

      <OrderHero
        line={heroLine}
        orderNumber={o.order_number}
        status={o.status}
        deliveryDate={o.delivery_date}
        pickupDate={o.pickup_date}
        daysRemaining={remaining}
        totalCents={o.total_cents}
        paymentState={o.payment_state}
      />

      {/* Audit P2 portal-order-detail — lifecycle stepper sits directly
          below the hero so the customer can see where the order is in
          its journey without reading any audit-log rows. */}
      <LifecycleStepper status={o.status} />

      {/* Phase 4c availability-procurement FR-5. Surfaces an admin
          proposed-change banner when the customer needs to accept or
          reject a different rental window. Falsy when no proposal. */}
      {payload.proposed_window ? (
        <ProposedWindowBanner orderNumber={orderNumber} proposal={payload.proposed_window} />
      ) : null}

      {o.status === "pending_procurement" && !payload.proposed_window ? (
        <ProcurementSourcingNotice />
      ) : null}

      {/* Phase 4d rental-expiry-reminders FR-7. */}
      {payload.active_expiry_reminder ? (
        <ExpiryReminderBanner
          orderNumber={orderNumber}
          reminder={payload.active_expiry_reminder}
        />
      ) : null}

      <ActionRail
        orderNumber={orderNumber}
        status={o.status}
        pickupDate={o.pickup_date}
        pickupNoticeAt={o.pickup_notice_at}
        agreementSigned={payload.agreement.signed}
        agreementPdfUrl={payload.agreement.pdf_url ?? null}
        onOpenExtension={onOpenExtension}
      />

      <SiteAddressCard address={o.site_address} lat={o.site_lat} lng={o.site_lng} />

      <Card title="Line items">
        {payload.lines.length === 0 ? (
          <Hint>No line items.</Hint>
        ) : (
          <ul className="space-y-3">
            {payload.lines.map((l) => (
              <LineItem key={l.id} line={l} />
            ))}
          </ul>
        )}
      </Card>

      {payload.timeline.length > 0 ? (
        <Card title="Timeline">
          <ol className="space-y-1.5">
            {payload.timeline.map((row, idx) => (
              <li key={`${row.action}-${idx}`} className="text-xs text-zinc-700">
                <span className="font-mono text-zinc-500">{formatDateTime(row.at)}</span>{" "}
                <span className="font-semibold text-brand-black">{humanizeAction(row.action)}</span>
              </li>
            ))}
          </ol>
        </Card>
      ) : null}

      {payload.invoices.length > 0 ? (
        <Card title="Invoices">
          <ul className="space-y-1.5">
            {payload.invoices.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between">
                <span className="font-medium text-brand-black">INV-{inv.id}</span>
                <span className="flex items-baseline gap-3">
                  <span className="tabular-nums">{formatMoneyCents(inv.total_cents)}</span>
                  <span className="text-xs text-zinc-500">{inv.status ?? "—"}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <CoiCard coi={payload.coi} />

      <TicketsCard
        tickets={payload.tickets}
        onOpenReporter={onOpenReporter}
      />

      {reporterOpen ? (
        <IssueReporterDialog
          orderNumber={orderNumber}
          onClose={onCloseReporter}
          onSubmitted={onCloseReporter}
        />
      ) : null}

      {extensionOpen && o.pickup_date ? (
        <ExtensionRequestSheet
          orderNumber={orderNumber}
          currentPickupDate={o.pickup_date}
          cardLast4={null}
          onClose={onCloseExtension}
        />
      ) : null}
    </div>
  );
}

function OrderHero({
  line,
  orderNumber,
  status,
  deliveryDate,
  pickupDate,
  daysRemaining,
  totalCents,
  paymentState
}: {
  line: OrderDetailLine | undefined;
  orderNumber: string;
  status: string;
  deliveryDate: string | null;
  pickupDate: string | null;
  daysRemaining: number | null;
  totalCents: number;
  paymentState: string | null;
}): JSX.Element {
  const heroName = line?.type_name ?? "Equipment";
  return (
    <header
      className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm md:p-6"
      data-testid="order-detail-hero"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">
        Order
      </p>
      <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start">
        <EquipmentThumb src={line?.image_url ?? null} name={heroName} size="lg" />
        <div className="flex-1">
          <h1 className="font-serif text-2xl font-bold italic tracking-tight text-brand-black md:text-3xl">
            {heroName}
          </h1>
          <p className="mt-1 font-mono text-xs text-zinc-500">#{orderNumber}</p>
          <span aria-hidden="true" className="mt-3 inline-block h-0.5 w-10 bg-brand-red" />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <StatusPill status={status} />
            <CountdownChip label="Pickup" days={daysRemaining} />
            {/* Audit P2 — surface order total in the hero so customers
                don't have to scroll to remember what they're paying. */}
            {totalCents > 0 ? (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-0.5 text-xs font-medium text-brand-black"
                data-testid="order-total-chip"
              >
                <span className="text-zinc-500">Total</span>
                <span className="tabular-nums">{formatMoneyCents(totalCents)}</span>
              </span>
            ) : null}
            <PaymentChip paymentState={paymentState} />
          </div>
          <p className="mt-3 text-xs text-zinc-600">
            Delivery <strong className="text-brand-black">{formatDate(deliveryDate)}</strong>{" "}
            · Pickup <strong className="text-brand-black">{formatDate(pickupDate)}</strong>
          </p>
        </div>
      </div>
    </header>
  );
}

function ActionRail({
  orderNumber,
  status,
  pickupDate,
  pickupNoticeAt,
  agreementSigned,
  agreementPdfUrl,
  onOpenExtension
}: {
  orderNumber: string;
  status: string;
  pickupDate: string | null;
  pickupNoticeAt: string | null;
  agreementSigned: boolean;
  agreementPdfUrl: string | null;
  onOpenExtension: () => void;
}): JSX.Element {
  const ready = useReadyForPickup(orderNumber);
  const canRequestPickup = READY_FOR_PICKUP_STATUSES.has(status);
  const alreadyPressed = pickupNoticeAt != null || ready.data?.already_notified === true;
  const canExtend = EXTENDABLE_STATUSES.has(status) && pickupDate;

  return (
    <section
      // Audit P2 — brand-red top hairline mirrors the AuthShell card
      // signature so the Action Rail reads as the most-actionable zone
      // on the page (visual hierarchy improvement called out in
      // .notes/design-grades/portal/02-order-detail.md item #8).
      className="relative flex flex-wrap items-center gap-2 overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-brand-red before:content-['']"
      aria-label="Order actions"
      data-testid="action-rail"
    >
      {canRequestPickup ? (
        <button
          type="button"
          disabled={alreadyPressed || ready.isPending}
          onClick={() => ready.mutate()}
          className="min-h-[44px] rounded-md bg-brand-red px-4 py-2 text-sm font-semibold uppercase tracking-wide text-brand-white shadow-sm transition hover:bg-brand-red-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="open-ready-for-pickup"
        >
          {alreadyPressed
            ? "Pickup requested ✓"
            : ready.isPending
              ? "Submitting…"
              : "Notify ready for pickup"}
        </button>
      ) : null}

      {canExtend ? (
        <button
          type="button"
          onClick={onOpenExtension}
          className="min-h-[44px] inline-flex items-center gap-1.5 rounded-md border border-zinc-300 bg-brand-white px-4 py-2 text-sm font-medium text-brand-black hover:border-brand-red hover:text-brand-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2"
          data-testid="open-extension-request"
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          Request extension
        </button>
      ) : null}

      {agreementSigned && agreementPdfUrl ? (
        <a
          href={agreementPdfUrl}
          target="_blank"
          rel="noreferrer"
          className="min-h-[44px] inline-flex items-center gap-1.5 rounded-md border border-zinc-300 bg-brand-white px-4 py-2 text-sm font-medium text-brand-black hover:border-brand-red hover:text-brand-red"
          data-testid="agreement-pdf-link"
        >
          <Download aria-hidden="true" className="h-4 w-4" />
          Agreement PDF
        </a>
      ) : null}

      <span className="text-xs text-zinc-500">
        Need to cancel? Call{" "}
        <a className="text-brand-red underline" href="tel:+13034354328">
          (303) 435-4328
        </a>
        .
      </span>
    </section>
  );
}

function SiteAddressCard({
  address,
  lat,
  lng
}: {
  address: OrderDetailPayload["order"]["site_address"];
  lat: number | null;
  lng: number | null;
}): JSX.Element | null {
  const parts = [address.line1, address.line2, address.city, address.state, address.postal_code]
    .filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <Card title="Site address">
      <p className="text-sm text-brand-black">{parts.join(", ")}</p>
      {/* Audit P2 — embedded Mapbox tile. Renders nothing when coords
          are absent or the token isn't configured (e.g. test env). */}
      <SiteMap lat={lat} lng={lng} />
    </Card>
  );
}

function LineItem({ line }: { line: OrderDetailLine }): JSX.Element {
  // Audit P2 portal-order-detail — surface the rate snapshot the
  // customer's accounting team always asks for. Format: "$1,000 ·
  // 7 days · Damage waiver included" beneath the equipment name.
  const dayLabel = line.duration_days === 1 ? "day" : "days";
  return (
    <li className="flex items-start gap-3">
      <EquipmentThumb src={line.image_url} name={line.type_name ?? "Equipment"} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <p className="font-medium text-brand-black">{line.type_name ?? "Equipment"}</p>
          <p
            className="tabular-nums text-sm font-semibold text-brand-black"
            data-testid="line-subtotal"
          >
            {formatMoneyCents(line.subtotal_cents)}
          </p>
        </div>
        <p className="text-xs text-zinc-500">
          {formatDate(line.rental_start_at)} → {formatDate(line.rental_end_at)}
          {line.duration_days > 0 ? (
            <>
              {" "}
              ·{" "}
              <span data-testid="line-duration">
                {line.duration_days} {dayLabel}
              </span>
            </>
          ) : null}
        </p>
        {line.damage_waiver ? (
          <p className="mt-1 text-xs text-emerald-700" data-testid="line-damage-waiver">
            <span aria-hidden="true">✓ </span>Damage waiver included
          </p>
        ) : null}
        {line.addons.length > 0 ? (
          <ul className="mt-1.5 list-disc pl-5 text-xs text-zinc-600">
            {line.addons.map((a, i) => (
              <li key={i}>
                {a.name} — {formatMoneyCents(a.amount_cents)}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
}

function CoiCard({
  coi
}: {
  coi: OrderDetailPayload["coi"];
}): JSX.Element | null {
  // Phase 4j portal-polish — hide when truly inapplicable (not required
  // and no waiver context). When required, show a clear approved /
  // pending / waived line.
  if (!coi.required && !coi.damage_waiver_opted_in) return null;

  let content: React.ReactNode;
  if (coi.damage_waiver_opted_in) {
    content = (
      <p className="text-sm text-emerald-700">
        <span aria-hidden="true">✓ </span>
        JDR damage waiver in place.
      </p>
    );
  } else if (coi.approved_at) {
    content = (
      <p className="text-sm text-emerald-700">
        <span aria-hidden="true">✓ </span>
        COI approved {formatDate(coi.approved_at)}.
      </p>
    );
  } else {
    content = (
      <p className="text-sm text-amber-800">
        COI required — upload from{" "}
        <Link to="/portal/account/ids" className="text-brand-red underline">
          Account → IDs
        </Link>
        .
      </p>
    );
  }
  return <Card title="Insurance">{content}</Card>;
}

function TicketsCard({
  tickets,
  onOpenReporter
}: {
  tickets: OrderDetailPayload["tickets"];
  onOpenReporter: () => void;
}): JSX.Element {
  return (
    <Card title="Support tickets">
      {tickets.length === 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-zinc-500">
            No tickets reported. See something off?
          </p>
          <button
            type="button"
            onClick={onOpenReporter}
            className="min-h-[44px] inline-flex items-center gap-1.5 rounded-md border border-zinc-300 bg-brand-white px-3 py-2 text-sm font-medium text-brand-black hover:border-brand-red hover:text-brand-red"
            data-testid="open-issue-reporter"
          >
            <MessageSquare aria-hidden="true" className="h-4 w-4" />
            Report an issue
          </button>
        </div>
      ) : (
        <>
          <ul className="space-y-2">
            {tickets.map((t) => (
              <li key={t.id} className="rounded-md border border-zinc-200 p-3">
                <div className="text-sm font-medium text-brand-black">{t.subject}</div>
                <div className="mt-0.5 text-xs text-zinc-500">
                  {t.severity} · {t.status} · {formatDateTime(t.created_at)}
                </div>
                {t.admin_reply ? (
                  <div className="mt-2 rounded bg-zinc-50 p-2 text-xs text-zinc-700">
                    <strong className="text-brand-black">JDR:</strong> {t.admin_reply}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={onOpenReporter}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brand-red hover:text-brand-red-dark hover:underline"
            data-testid="open-issue-reporter"
          >
            <MessageSquare aria-hidden="true" className="h-4 w-4" />
            Report another issue
          </button>
        </>
      )}
    </Card>
  );
}

function Card({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-lg border border-zinc-200 bg-brand-white p-4 shadow-sm">
      <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-red">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Hint({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className="text-sm text-zinc-500">{children}</p>;
}

function ProcurementSourcingNotice(): JSX.Element {
  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-4"
      data-testid="procurement-sourcing-notice"
    >
      <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <FileText aria-hidden="true" className="h-4 w-4" />
        JDR is sourcing your equipment
      </p>
      <p className="mt-1 text-sm text-amber-900">
        We don&rsquo;t currently have one of these in stock, but{" "}
        <strong>we&rsquo;re working on getting one</strong>. We&rsquo;ll confirm
        within 24 hours and email you. The hold on your card stays in place;
        we won&rsquo;t capture funds until your rental is confirmed.
      </p>
    </div>
  );
}

const ACTION_LABEL: Record<string, string> = {
  "order.confirmed": "Order confirmed",
  pickup_notice: "Pickup requested",
  "delivery.checkin_submitted": "Delivery checked in",
  "pickup.completed": "Pickup completed",
  "order.cancelled": "Order cancelled"
};

function humanizeAction(action: string): string {
  return ACTION_LABEL[action] ?? action.replace(/[._]/g, " ");
}
